import { useMemo } from 'react'
// The only production import of the library; callers use this hook instead.
// eslint-disable-next-line no-restricted-imports
import { useDraggable as useLibraryDraggable } from 'react-use-draggable-scroll'

type Options = Parameters<typeof useLibraryDraggable>[1]

let detached: HTMLElement | null = null
const detachedElement = (): HTMLElement =>
  (detached ??= document.createElement('div'))

/**
 * react-use-draggable-scroll's `useDraggable`, safe once its element has left
 * the page.
 *
 * The library adds mouseup, mousemove and resize listeners to the whole
 * window while `isMounted` is true. Its mouseup and resize handlers read
 * `ref.current` on every event (mousemove only mid-drag, and so do its
 * momentum timers). A carousel rendered only some of the time leaves that
 * null, and `isMounted: !!ref.current` does not help: it is still true on the
 * render that removes the element, so the listeners outlive it and every
 * click anywhere on the page throws. While the element is missing, the
 * library gets an empty detached div, which it can measure and style without
 * effect.
 */
export function useDraggable(
  ref: { readonly current: HTMLElement | null | undefined },
  options?: Options
) {
  const safeRef = useMemo(
    () => ({
      get current(): HTMLElement {
        return ref.current ?? detachedElement()
      },
    }),
    [ref]
  )
  return useLibraryDraggable(safeRef, options)
}
